import { Bell } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { mixpanelOpenNotification } from "@/lib/tracker";
import { useAuth } from "@/services/context/auth/AuthContext";
import { getNotifications } from "@/services/controllers/notifications";
import { useAppDispatch, useAppSelector } from "@/services/hooks/redux.hooks";
import {
  setNotifications,
  updateNotificationBadge,
} from "@/services/redux/notifications.slice";

import ExchangeImage from "./global/exchange-swticher/exchangeIcon";
import { Skeleton } from "./ui/skeleton";

export default function NotificationsDropdown() {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { hasNewNotifications, notifications } = useAppSelector(
    (state) => state.notifications,
  );
  const { loadingKeys } = useAppSelector((state) => state.exchangeSwitcher);
  const { language, loading: userLoading } = useAppSelector(
    (state) => state.user,
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [isOpened, setIsOpened] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();
    if ((!loadingKeys && !userLoading && !isOpened) || isOpened)
      getNotifications({
        signal: controller.signal,
        params: {
          skip: 0,
          limit: 10,
          order_by: "desc",
        },
      })
        .then(() => setLoading(false))
        .catch((error) => {
          if (error.code !== "ERR_CANCELED" && error.code != null) {
            setLoading(false);
          }
        });
    return () => {
      controller.abort();
    };
  }, [loadingKeys, language, userLoading, isOpened]);

  return (
    <Popover
      onOpenChange={(open) => {
        setIsOpened(open);
        if (open && user) {
          mixpanelOpenNotification(user?.uid);
        }
        if (hasNewNotifications) {
          dispatch(updateNotificationBadge(false));
        } else {
          dispatch(
            setNotifications(
              notifications.map((e) => ({ ...e, is_seen: true })),
            ),
          );
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="rounded-full relative"
        >
          <Bell className="w-4 h-4 text-gray-900 dark:text-gray-50" />
          <span className="sr-only">Notifications</span>
          {hasNewNotifications && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0 max-w-[400px] md:border md:rounded-lg rounded-sm border-0"
        align="end"
      >
        <Card className="shadow-none border-0 hover:shadow-md transition-shadow">
          <CardHeader className="border-b pb-2">
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 max-h-[300px] overflow-auto !max-w-screen pt-4">
            {loading ? (
              <>
                <div className="flex items-start gap-4 rounded-sm transition-all">
                  <div>
                    <Skeleton className="w-8 h-8" />
                  </div>
                  <div className="">
                    <Skeleton className="w-[100px] h-[20px] mb-1" />
                    <Skeleton className="w-[300px] h-[20px] mb-1" />
                    <Skeleton className="w-[60px] h-[14px]" />
                  </div>
                </div>
                <div className="flex items-start gap-4 rounded-sm transition-all">
                  <div>
                    <Skeleton className="w-8 h-8" />
                  </div>
                  <div className="">
                    <Skeleton className="w-[100px] h-[20px] mb-1" />
                    <Skeleton className="w-[300px] h-[20px] mb-1" />
                    <Skeleton className="w-[60px] h-[14px]" />
                  </div>
                </div>
                <div className="flex items-start gap-4 rounded-sm transition-all">
                  <div>
                    <Skeleton className="w-8 h-8" />
                  </div>
                  <div className="">
                    <Skeleton className="w-[100px] h-[20px] mb-1" />
                    <Skeleton className="w-[300px] h-[20px] mb-1" />
                    <Skeleton className="w-[60px] h-[14px]" />
                  </div>
                </div>
              </>
            ) : notifications.length ? (
              notifications.map((notification) => {
                return (
                  <div
                    key={notification.id}
                    className={twMerge(
                      notification.is_seen !== "false" ? "" : "bg-gray-100",
                      "flex items-start gap-4 rounded-sm hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-50 ",
                    )}
                  >
                    <div className="flex h-8 w-8 items-center justify-center text-blue-500 relative">
                      <ExchangeImage
                        providerId={notification.provider_id}
                        width={24}
                      />
                    </div>
                    <div className="flex-1 max-w-full overflow-hidden">
                      <h4 className="text-sm font-medium">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 ">
                        {notification.body}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {moment(
                          new Date(notification.created_time || ""),
                        ).fromNow()}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4 max-w-[400px]">
                <Bell className="h-12 w-12 text-gray-400" />
                <div className="space-y-2 text-center">
                  <h3 className="text-lg font-semibold">No Notifications</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {
                      "You don't have any notifications yet. When you do, they'll show up here."
                    }
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
}
